// Фасад над adminApi.call() с единым контрактом и опциями  
import { asList, asSingle } from './adapters';
import { adminApi, type ApiResult } from '@/lib/admin/adminApi'; // v2 client for fail-closed session handling
import type { SourceTag } from './types';

// Security note: This facade is admin-only and should only be called from authenticated admin contexts

type RpcOptions = {
  token?: string;
  idempotent?: boolean;
  timeoutMs?: number;
  retryCount?: number;
  showErrorToast?: boolean;
  showSuccessToast?: boolean;
  successMessage?: string;
  logLabel?: string;
  allowOnPublic?: boolean;
};

type ActionSuccess<TRes> = { ok: true; data: TRes; source: SourceTag; disabled: false };
type ActionError = { ok: false; error: unknown; source: SourceTag; disabled: false; code?: string; traceId?: string };

export async function callAction<TRes = any, TReq = any>(fn: string, payload?: TReq, opts: RpcOptions = {}): Promise<ActionSuccess<TRes> | ActionError> {
  const callOptions = {
    timeoutMs: opts.timeoutMs || 15000,
    retryCount: opts.retryCount ?? 1, // Actions should be cautious with retries
    ...(opts.token ? { token: opts.token } : {}),
    ...(opts.allowOnPublic ? { allowOnPublic: opts.allowOnPublic } : {})
  };

  const res: ApiResult<TRes> = await adminApi.call<any, TRes>(fn, payload || {} as any, callOptions);
  if (!res.ok) {
    const failureResult = res as { ok: false; code: string; error: string; traceId: string; details?: unknown };
    return {
      ok: false as const,
      error: failureResult.error || 'RPC failed',
      source: 'action' as SourceTag,
      disabled: false,
      code: failureResult.code,
      traceId: failureResult.traceId,
    };
  }
  return { ok: true as const, data: res.data, source: 'action' as SourceTag, disabled: false as const };
}

export async function callList<TRes = any, TReq = any>(fn: string, payload?: TReq, opts: RpcOptions = {}) {
  const callOptions = {
    timeoutMs: opts.timeoutMs || 15000,
    retryCount: opts.retryCount ?? 2, // Lists can retry more safely
    ...(opts.token ? { token: opts.token } : {}),
    ...(opts.allowOnPublic ? { allowOnPublic: opts.allowOnPublic } : {})
  };
  
  const res: ApiResult<TRes> = await adminApi.call<any, TRes>(fn, payload || {} as any, callOptions);
  if (!res.ok) {
    const failureResult = res as { ok: false; code: string; error: string; traceId: string; details?: unknown };
    const errorResult = { ok: false, error: failureResult.error || 'RPC failed', source: 'list', code: failureResult.code, traceId: failureResult.traceId };
    return asList<TRes>(errorResult);
  }
  return asList<TRes>({ ...res, source: 'list' });
}

export async function callSingle<TRes = any, TReq = any>(fn: string, payload?: TReq, opts: RpcOptions = {}) {
  // Fast timeout for critical admin functions
  const defaultTimeout = fn === 'is_admin_simple' ? 3000 : 15000;
  const callOptions = {
    timeoutMs: opts.timeoutMs || defaultTimeout,
    retryCount: opts.retryCount ?? 2, // Singles can retry safely
    ...(opts.token ? { token: opts.token } : {}),
    ...(opts.allowOnPublic ? { allowOnPublic: opts.allowOnPublic } : {})
  };
  const res: ApiResult<TRes> = await adminApi.call<any, TRes>(fn, payload || {} as any, callOptions);
  if (!res.ok) {
    const failureResult = {
      ...res,
      source: 'single' as SourceTag,
    };
    return asSingle<TRes>(failureResult);
  }

  return asSingle<TRes>({ ...res, source: 'single' as SourceTag });
}